/**
 * MapDef 데이터를 실제 지형(물리 바디 + 타일 스프라이트 + 배경)으로 만든다.
 * 맵마다 씬을 만들지 않고 데이터만 바꾸면 되도록 모든 생성을 여기로 모았다.
 */

import Phaser from 'phaser';
import { ANIM } from '../assets/animations';
import type { AssetTable } from '../assets/loader';
import type { ClimbableDef, MapDef, PortalDef } from '../data/maps';
import { DEPTH } from '../game/depth';
import { UI_FONT } from '../ui/theme';

export interface ClimbableInstance extends ClimbableDef {
  /** 등반 판정용 사각형 */
  rect: Phaser.Geom.Rectangle;
}

export interface PortalInstance extends PortalDef {
  sprite: Phaser.GameObjects.Sprite;
}

export interface Terrain {
  platforms: Phaser.Physics.Arcade.StaticGroup;
  climbables: ClimbableInstance[];
  portals: PortalInstance[];
}

/** 등반 판정 폭. 원작처럼 넉넉하게 잡아 사다리 앞에서 ↑ 만 눌러도 잡히게 한다. */
const CLIMB_GRAB_WIDTH = 58;

export function buildTerrain(scene: Phaser.Scene, map: MapDef, assets: AssetTable): Terrain {
  buildBackground(scene, map);

  const platforms = scene.physics.add.staticGroup();
  const grassHeight = assets.get('tile-grass').frameHeight;
  const platformHeight = assets.get('tile-platform').frameHeight;

  for (const def of map.platforms) {
    const oneWay = def.oneWay !== false;
    const visualHeight = oneWay ? platformHeight : grassHeight;

    if (oneWay) {
      const tile = scene.add.tileSprite(def.x, def.y, def.w, visualHeight, 'tile-platform');
      tile.setOrigin(0, 0).setDepth(DEPTH.terrain).setTint(map.palette.tileTint);
    } else {
      const soilTop = def.y + grassHeight;
      const soil = scene.add.tileSprite(
        def.x,
        soilTop,
        def.w,
        Math.max(0, map.height - soilTop),
        'tile-soil',
      );
      soil.setOrigin(0, 0).setDepth(DEPTH.terrain).setTint(map.palette.tileTint);
      const grass = scene.add.tileSprite(def.x, def.y, def.w, grassHeight, 'tile-grass');
      grass.setOrigin(0, 0).setDepth(DEPTH.terrain).setTint(map.palette.tileTint);
    }

    // 물리 바디: 표면 두께만큼만 만든다 (아래에서 뚫고 올라오지 않게 바닥 지형은 두껍게)
    const bodyHeight = oneWay ? 16 : Math.max(24, map.height - def.y);
    const body = scene.add.rectangle(
      def.x + def.w / 2,
      def.y + bodyHeight / 2,
      def.w,
      bodyHeight,
    );
    body.setVisible(false);
    scene.physics.add.existing(body, true);
    platforms.add(body);

    const arcade = body.body as Phaser.Physics.Arcade.StaticBody;
    if (oneWay) {
      arcade.checkCollision.down = false;
      arcade.checkCollision.left = false;
      arcade.checkCollision.right = false;
    }
    body.setData('oneWay', oneWay);
    body.setData('surfaceY', def.y);
  }

  const climbables: ClimbableInstance[] = map.climbables.map((def) => {
    const texture = def.kind === 'ladder' ? 'climb-ladder' : 'climb-rope';
    const frame = assets.get(texture);
    const height = def.yBottom - def.yTop;
    const tile = scene.add.tileSprite(def.x, def.yTop, frame.frameWidth, height, texture);
    tile.setOrigin(0.5, 0).setDepth(DEPTH.climbable).setTint(map.palette.tileTint);

    return {
      ...def,
      rect: new Phaser.Geom.Rectangle(
        def.x - CLIMB_GRAB_WIDTH / 2,
        def.yTop - 16,
        CLIMB_GRAB_WIDTH,
        height + 32,
      ),
    };
  });

  const portals: PortalInstance[] = map.portals.map((def) => {
    const sprite = scene.add.sprite(def.x, def.y + 6, 'portal', 0);
    sprite.setOrigin(0.5, 1).setDepth(DEPTH.portal).play(ANIM.portal);
    const label = scene.add.text(def.x, def.y - 118, `↑ ${def.label}`, {
      fontFamily: UI_FONT,
      fontSize: '14px',
      color: '#ffffff',
      stroke: '#1b1b28',
      strokeThickness: 4,
      align: 'center',
      wordWrap: { width: 200, useAdvancedWrap: true },
    });
    label.setOrigin(0.5, 1).setDepth(DEPTH.portal);
    return { ...def, sprite };
  });

  for (const def of map.decor) {
    const sprite = scene.add.image(def.x, def.y + 4, def.texture);
    sprite
      .setOrigin(0.5, 1)
      .setDepth(DEPTH.decor)
      .setScale((def.scale ?? 1) * (def.flip ? -1 : 1), def.scale ?? 1)
      .setAlpha(def.alpha ?? 1)
      .setTint(map.palette.decorTint);
  }

  return { platforms, climbables, portals };
}

function buildBackground(scene: Phaser.Scene, map: MapDef): void {
  const { width, height } = scene.scale;

  const sky = scene.add.graphics();
  sky.setScrollFactor(0).setDepth(DEPTH.sky);
  const steps = 24;
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    const color = Phaser.Display.Color.Interpolate.ColorWithColor(
      Phaser.Display.Color.IntegerToColor(map.palette.skyTop),
      Phaser.Display.Color.IntegerToColor(map.palette.skyBottom),
      1,
      t,
    );
    sky.fillStyle(Phaser.Display.Color.GetColor(color.r, color.g, color.b), 1);
    sky.fillRect(0, (height / steps) * i, width, height / steps + 1);
  }

  // 원경 / 근경 언덕
  drawHills(scene, map.width, 0.18, map.palette.farHill, 470, 150, DEPTH.farHill, 7);
  drawHills(scene, map.width, 0.38, map.palette.nearHill, 540, 120, DEPTH.nearHill, 5);

  // 구름
  for (let i = 0; i < 8; i++) {
    const cloud = scene.add.image(
      Phaser.Math.Between(0, map.width),
      Phaser.Math.Between(50, 260),
      i % 2 === 0 ? 'decor-cloud-a' : 'decor-cloud-b',
    );
    cloud
      .setScrollFactor(0.22)
      .setDepth(DEPTH.cloud)
      .setAlpha(0.85)
      .setScale(Phaser.Math.FloatBetween(0.7, 1.3))
      .setTint(map.palette.cloud);
    scene.tweens.add({
      targets: cloud,
      x: cloud.x + Phaser.Math.Between(120, 260),
      duration: Phaser.Math.Between(26000, 44000),
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
  }

  // 전체 분위기 오버레이
  const ambient = scene.add.rectangle(0, 0, width, height, map.palette.ambient);
  ambient
    .setOrigin(0, 0)
    .setScrollFactor(0)
    .setAlpha(map.palette.ambientAlpha)
    .setDepth(DEPTH.ambient)
    .setBlendMode(Phaser.BlendModes.ADD);
}

function drawHills(
  scene: Phaser.Scene,
  worldWidth: number,
  scrollFactor: number,
  color: number,
  baseY: number,
  amplitude: number,
  depth: number,
  count: number,
): void {
  const g = scene.add.graphics();
  g.setScrollFactor(scrollFactor).setDepth(depth);
  g.fillStyle(color, 1);
  const span = worldWidth * scrollFactor + scene.scale.width;
  const step = span / count;
  for (let i = 0; i <= count; i++) {
    const cx = i * step;
    const rx = step * 0.85;
    const ry = amplitude * (0.7 + ((i * 37) % 10) / 20);
    g.fillEllipse(cx, baseY, rx * 2, ry * 2);
  }
  g.fillRect(0, baseY, span, scene.scale.height - baseY);
}
